import * as THREE from "three";
import { gsap } from "gsap";
import { primaryColor, secondaryColor, white, brown } from "./styles";
import { channelJourney, interactedObjects } from "../World/sets";
import LineElement from "../World/LineElement";
import Experience from "../Experience";
import CameraPathTracker from "../World/CameraPathTracker";
import { debugLog } from "./debugLog";

type InteractiveObject = THREE.Object3D & {
	name: string;
	visible: boolean;
	userData: InteractiveUserData;
	slug?: string;
	color?: string;
};
type InteractiveInstance = NonNullable<InteractiveUserData["instance"]>;
type InteractiveUserData = {
	clicked?: boolean;
	active?: boolean;
	blockType?: string;
	direction?: THREE.Vector3;
	position?: THREE.Vector3;
	data?: {
		id?: string;
		gltfURL?: string;
	};
	instance?: {
		updatePosition?: (position: THREE.Vector3) => void;
		onHover: (...args: unknown[]) => void;
		onClick: () => Promise<void> | void;
		changeSpriteColor: (...args: unknown[]) => void;
	};
};

export default class Controls {
	experience: Experience;
	canvas: HTMLCanvasElement | null;
	sizes: { width: number; height: number };
	camera: Experience["camera"];
	scene: THREE.Scene;
	raycaster: THREE.Raycaster;
	mouse: THREE.Vector2;
	mDown: boolean;
	mDragging: boolean;
	rightClick: boolean;
	currentIntersect: InteractiveObject | null;
	syntheticClick: boolean;
	startTimeout: ReturnType<typeof setTimeout> | null;
	connectionManager: Experience["connectionManager"];
	firstClick: boolean;
	private readonly onMouseUpHandler: (event: MouseEvent) => void;
	private readonly onContextMenuHandler: (event: MouseEvent) => void;
	private readonly onMouseDownHandler: (event: MouseEvent) => void;
	private readonly onMouseMoveHandler: (event: MouseEvent) => void;
	private readonly onMouseClickHandler: (event: MouseEvent) => void;
	private readonly onKeyDownHandler: (event: KeyboardEvent) => void;
	private readonly onKeyUpHandler: (event: KeyboardEvent) => void;
	private lastIntersectionCheck = 0;
	private readonly intersectionThrottleMs = 50;
	private intersectionCandidates: InteractiveObject[] = [];
	private intersectionCandidatesDirty = true;
	private lastKnownRootChildrenCount = -1;
	private readonly useFrustumPrefilter = true;
	private readonly raycastFrustum = new THREE.Frustum();
	private readonly raycastProjectionMatrix = new THREE.Matrix4();

	constructor(_experience: Experience) {
		this.experience = _experience;
		this.canvas = this.experience.canvas;
		this.sizes = this.experience.sizes;
		this.camera = this.experience.camera;
		this.scene = this.experience.scene;
		this.raycaster = new THREE.Raycaster();
		this.mouse = new THREE.Vector2(-this.sizes.width, this.sizes.height);
		this.mDown = false;
		this.mDragging = false;
		this.rightClick = false;
		this.currentIntersect = null;
		this.syntheticClick = false;
		this.startTimeout = null;
		this.connectionManager = this.experience.connectionManager;
		this.firstClick = false;
		this.onMouseUpHandler = this.onMouseUp.bind(this);
		this.onContextMenuHandler = this.onContextMenu.bind(this);
		this.onMouseDownHandler = this.onMouseDown.bind(this);
		this.onMouseMoveHandler = this.onMouseMove.bind(this);
		this.onMouseClickHandler = this.onMouseClick.bind(this);
		this.onKeyDownHandler = this.onKeyDown.bind(this);
		this.onKeyUpHandler = this.onKeyUp.bind(this);
		this.init();
	}

	private getInstance(object: InteractiveObject): InteractiveInstance | null {
		return object.userData.instance ?? null;
	}
	private fitSpriteTarget(target: THREE.Object3D, multiplier = 1) {
		return this.experience.sceneOperations.fitToBoxWithPadding(
			target,
			"sprite",
			multiplier,
		);
	}
	private async fitTitleSprite(multiplier = 1) {
		const titleSprite = this.scene.getObjectByName("TitleSprite");
		if (!titleSprite) return false;
		await this.fitSpriteTarget(titleSprite, multiplier);
		return true;
	}
	private updateMouseFromEvent(event: MouseEvent) {
		this.mouse.set(
			(event.clientX / this.sizes.width) * 2 - 1,
			-(event.clientY / this.sizes.height) * 2 + 1,
		);
	}
	private maybeCheckIntersections(force = false) {
		const now = performance.now();
		if (!force && now - this.lastIntersectionCheck < this.intersectionThrottleMs) {
			return;
		}
		this.lastIntersectionCheck = now;
		this.checkIntersections();
	}
	invalidateIntersectionCandidates() {
		this.intersectionCandidatesDirty = true;
	}
	private refreshIntersectionCandidatesIfNeeded() {
		const rootChildrenCount = this.scene.children.length;
		if (!this.intersectionCandidatesDirty && this.lastKnownRootChildrenCount === rootChildrenCount) {
			return;
		}
		const nextCandidates: InteractiveObject[] = [];
		this.scene.traverse((object) => {
			if (object.type === "Sprite" || object.type === "Mesh") {
				nextCandidates.push(object as InteractiveObject);
			}
		});
		this.intersectionCandidates = nextCandidates;
		this.lastKnownRootChildrenCount = rootChildrenCount;
		this.intersectionCandidatesDirty = false;
	}
	private getRaycastTargets(): InteractiveObject[] {
		this.refreshIntersectionCandidatesIfNeeded();
		if (!this.useFrustumPrefilter) {
			return this.intersectionCandidates;
		}
		const cameraInstance = this.camera.instance;
		cameraInstance.updateMatrixWorld();
		this.raycastProjectionMatrix.multiplyMatrices(
			cameraInstance.projectionMatrix,
			cameraInstance.matrixWorldInverse,
		);
		this.raycastFrustum.setFromProjectionMatrix(this.raycastProjectionMatrix);
		const visibleCandidates = this.intersectionCandidates.filter((candidate) =>
			this.raycastFrustum.intersectsObject(candidate),
		);
		return visibleCandidates.length > 0 ? visibleCandidates : this.intersectionCandidates;
	}

	init() {
		window.addEventListener("mouseup", this.onMouseUpHandler);
		window.addEventListener("contextmenu", this.onContextMenuHandler);
		window.addEventListener("mousedown", this.onMouseDownHandler);
		window.addEventListener("mousemove", this.onMouseMoveHandler);
		window.addEventListener("click", this.onMouseClickHandler);
		window.addEventListener("keydown", this.onKeyDownHandler);
		window.addEventListener("keyup", this.onKeyUpHandler);
	}

	destroy() {
		window.removeEventListener("mouseup", this.onMouseUpHandler);
		window.removeEventListener("contextmenu", this.onContextMenuHandler);
		window.removeEventListener("mousedown", this.onMouseDownHandler);
		window.removeEventListener("mousemove", this.onMouseMoveHandler);
		window.removeEventListener("click", this.onMouseClickHandler);
		window.removeEventListener("keydown", this.onKeyDownHandler);
		window.removeEventListener("keyup", this.onKeyUpHandler);
	}

	onMouseUp(event: MouseEvent) {
		if (event.button === 2 && this.rightClick) {
			this.rightClick = false;
			if (this.mDragging) {
				this.mDragging = !this.mDragging;
			}
			this.mDown = false;
		}
		if (event.button === 1) {
			this.experience.pathview.removeFilter();
			this.experience.pathview.getUpcomingConnectionsBoundingBox();
		}
	}

	onContextMenu(event: MouseEvent) {
		event.preventDefault();
		if (this.currentIntersect) {
			this.experience.uiHandlers.updateContextMenu(this.currentIntersect);
			this.experience.uiHandlers.showContextMenu(event, this.currentIntersect);
		} else {
			this.experience.uiHandlers.hideContextMenu();
		}
	}

	onMouseDown(event: MouseEvent) {
		this.mDown = true;
		if (event.button === 2) {
			this.rightClick = true;
			event.preventDefault();
			this.experience.uiHandlers.updateContextMenu(this.currentIntersect);
		} else {
			this.rightClick = false;
		}
		if (event.button !== 2 && !this.currentIntersect) {
			const target = event.target as HTMLElement;
			if (target.tagName !== "A" && target.tagName !== "CANVAS") {
				this.experience.uiHandlers.hideContextMenu();
			}
		}
		if (event.button === 1) {
			this.experience.pathview.filterPath(null);
			this.experience.pathview.getPathBoundingBox();
		}
	}

	onMouseMove(event: MouseEvent) {
		if (this.startTimeout) {
			this.experience.resetTimeout();
		}
		this.updateMouseFromEvent(event);
		if (this.mDown) {
			this.mDragging = true;
			this.experience.uiHandlers.hideContextMenu();
		}
		this.maybeCheckIntersections();
	}

	onMouseClick(event: MouseEvent) {
		this.updateMouseFromEvent(event);
		this.maybeCheckIntersections(true);
		if (!this.mDragging) {
			void this.handleMouseClick(event);
		}
		this.mDown = false;
		this.mDragging = false;
	}

	checkIntersections() {
		this.raycaster.setFromCamera(this.mouse, this.camera.instance);
		const objectsToTest = this.getRaycastTargets();

		if (!this.mDragging) {
			const intersects = this.raycaster.intersectObjects(objectsToTest, true);
			if (intersects.length > 0) {
				if (intersects[0].object.name !== "pathwayItem") {
					const rootEl = document.querySelector("html,body") as HTMLElement | null;
					if (rootEl) rootEl.style.cursor = "pointer";
				}
				const intersectedObject = intersects[0].object;
				if (intersectedObject !== this.currentIntersect) {
					if (this.currentIntersect) {
						this.onMouseLeave(this.currentIntersect);
					}
					this.currentIntersect = intersectedObject;
					this.onMouseEnter(this.currentIntersect);
				}
			} else {
				const rootEl = document.querySelector("html,body") as HTMLElement | null;
				if (rootEl) rootEl.style.cursor = "auto";
				if (this.currentIntersect) {
					this.onMouseLeave(this.currentIntersect);
					this.currentIntersect = null;
				}
			}
		}
	}

	onMouseEnter(object: InteractiveObject) {
		this.experience.pathview.filterPath(object);
		if (object.visible) {
			if (
				this.experience.sceneOperations.autorotate &&
				!object.name.includes("TaglineSprite") &&
				!object.name.includes("TitleSprite") &&
				!this.firstClick
			) {
				this.experience.sceneOperations.autorotate = false;
			}
			switch (object.name) {
				case "TitleSprite":
					object.color = secondaryColor;
					break;
				case "wanderSprite":
					this.experience.uiHandlers.changeTitleSpriteText("wander");
					break;
				case " ? Sprite":
					this.experience.uiHandlers.changeTitleSpriteText("about");
					break;
				case "anywhereSprite":
					this.experience.uiHandlers.changeTitleSpriteText("anywhere");
					break;
				case "somewhereSprite":
					this.experience.uiHandlers.changeTitleSpriteText("somewhere");
					break;
				case "walkthroughSprite":
					this.experience.uiHandlers.changeTitleSpriteText("walkthrough");
					break;
				default:
					break;
			}
			this.experience.sceneUtils.showBlockInfo(object);
			const objectInstance = this.getInstance(object);
			if (object.type === "Mesh" && objectInstance) {
				objectInstance.updatePosition?.(object.position);
				objectInstance.onHover(objectInstance);
				gsap.to(object.scale, {
					duration: 0.3,
					x: 1.1,
					y: 1.1,
					z: 1.1,
					ease: "power2.out",
				});
			}
			if (object.type === "Sprite" && objectInstance) {
				objectInstance.onHover();
				if (object.name.includes("Sprite")) {
					if (object.name === "wanderSprite") {
						objectInstance.changeSpriteColor(object, primaryColor, brown);
					} else if (object.name === "PathwayItemSprite") {
						objectInstance.changeSpriteColor(object, primaryColor, brown);
						this.experience.uiHandlers.hoverPathwayItem(object);
					} else {
						objectInstance.changeSpriteColor(object, secondaryColor, brown);
					}
				}
				if (object.userData.blockType === "Channel") {
					objectInstance.changeSpriteColor(object, secondaryColor, brown);
				}
				if (object.userData.blockType === "Text") {
					objectInstance.changeSpriteColor(object, white, brown, primaryColor);
				}
			}
		}
	}

	onMouseLeave(object: InteractiveObject) {
		if (object.visible) {
			switch (object.name) {
				case "TitleSprite":
					object.color = primaryColor;
					if (!this.experience.sceneOperations.autorotate) {
						this.experience.sceneOperations.autorotate = true;
					}
					break;
				case "wanderSprite":
				case " ? Sprite":
				case "pathsSprite":
				case "TaglineSprite":
				case "anywhereSprite":
				case "somewhereSprite":
				case "walkthroughSprite":
					if (!this.experience.sceneOperations.autorotate && !object?.userData?.clicked) {
						this.experience.sceneOperations.autorotate = true;
					}
					this.experience.uiHandlers.changeTitleSpriteText("Pathways");
					break;
				default:
					if (!this.experience.sceneOperations.autorotate && !this.firstClick) {
						this.experience.sceneOperations.autorotate = true;
					}
					break;
			}
			this.experience.sceneUtils.hideBlockInfo(object);
			const objectInstance = this.getInstance(object);
			if (object.type === "Mesh" && objectInstance) {
				gsap.to(object.scale, { duration: 0.3, x: 1, y: 1, z: 1, ease: "power2.inOut" });
				if (!this.experience.sceneOperations.autorotate && !this.firstClick) {
					this.experience.sceneOperations.autorotate = true;
				}
			}
			if (object.type === "Sprite" && objectInstance) {
				if (object.name.includes("Sprite") && object.userData.clicked === true) {
					objectInstance.changeSpriteColor(object, primaryColor, brown);
				} else if (object.name.includes("Sprite")) {
					objectInstance.changeSpriteColor(object, brown, white);
					if (object.name === "PathwayItemSprite") {
						this.experience.uiHandlers.hoverPathwayItem(object, false);
					}
				}
				if (object.userData.blockType === "Channel") {
					if (!object.userData.active) {
						objectInstance.changeSpriteColor(object, brown, white);
					} else {
						objectInstance.changeSpriteColor(object, primaryColor, brown);
					}
				}
				if (object.userData.blockType === "Text") {
					objectInstance.changeSpriteColor(object, white, brown);
				}
			}
		}
		this.experience.pathview.removeFilter();
	}

	onKeyDown(event: KeyboardEvent) {
		if (event.key === "ArrowLeft") {
			this.experience.pathview.previousCrossRoad();
		}
		if (event.key === "ArrowRight") {
			this.experience.pathview.nextCrossRoad();
		}
		if (event.key === "Escape") {
			this.experience.uiHandlers.hideContextMenu();
			this.experience.uiHandlers.hidePopup();
			this.experience.uiHandlers.toggleInfoPage();
			void this.fitTitleSprite();
			const infoSprite = this.scene.getObjectByName(" ? Sprite") as
				| InteractiveObject
				| undefined;
			if (infoSprite) {
				const infoInstance = this.getInstance(infoSprite);
				infoSprite.userData.clicked = false;
				infoInstance?.changeSpriteColor(infoSprite, brown, white);
			}
		}
		if (event.key === "o") {
			this.experience.uiHandlers.sceneOverview();
		}
		if (event.key === " ") {
			this.experience.pathview.filterPath(null);
			this.experience.pathview.getPathBoundingBox();
		}
		if (event.key === "r") {
			location.reload();
		}
		if (event.key === "f") {
			if (this.currentIntersect) {
				if (this.currentIntersect.type === "Sprite") {
					this.fitSpriteTarget(this.currentIntersect);
				} else if (this.currentIntersect.type === "Mesh") {
					this.camera.fitToRect(this.currentIntersect, this.camera.paddingMesh);
				}
			}
		}
		if (event.key === "t") {
			const titleScreen = this.experience.uiHandlers.titleScreen;
			if (titleScreen.classList.contains("active")) {
				gsap.to(titleScreen, {
					duration: 0.6,
					opacity: 0,
					ease: "power2.inOut",
					onComplete: () => {
						titleScreen.style.display = "none";
						titleScreen.classList.remove("active");
						this.camera.getCameraControls().dolly(100, true);
					},
				});
			} else {
				titleScreen.style.display = "flex";
				titleScreen.style.opacity = "0";
				titleScreen.classList.add("active");
				gsap.to(titleScreen, {
					onStart: () => {
						this.camera.getCameraControls().dolly(-100, true);
					},
					duration: 0.6,
					opacity: 1,
					ease: "power2.inOut",
				});
			}
		}
		if (event.key === "a") {
			this.experience.sceneOperations.autorotate =
				!this.experience.sceneOperations.autorotate;
		}
	}

	onKeyUp(event: KeyboardEvent) {
		if (event.key === " ") {
			this.experience.pathview.removeFilter();
			this.experience.pathview.getUpcomingConnectionsBoundingBox();
		}
	}

	async handleMouseClick(_event: MouseEvent) {
		if (this.syntheticClick) {
			this.syntheticClick = false;
			return;
		}
		if (this.rightClick) return;
		const intersect = this.currentIntersect;
		if (intersect && intersect.visible) {
			this.firstClick = true;
			if (
				this.experience.uiHandlers.restartAnywhereElseBtn.classList.contains("active")
			) {
				this.experience.uiHandlers.restartAnywhereElseBtn.classList.remove("active");
				this.experience.uiHandlers.saveBtn.classList.add("active", "disabled");
			}
			if (
				this.experience.uiHandlers.saveBtn.classList.contains("active") &&
				this.experience.uiHandlers.saveBtn.classList.contains("disabled") &&
				channelJourney.length > 2
			) {
				this.experience.uiHandlers.saveBtn.classList.remove("disabled");
			}
			if (intersect.name === "TitleSprite") {
				if (!intersect.userData?.clicked) {
					intersect.userData.clicked = true;
					this.fitSpriteTarget(intersect, 2);
					await this.experience.sceneOperations.initLanding();
				}
			}
			this.experience.uiHandlers.stopLandingAlert();
			if (intersect.position) {
				intersect.userData.position = intersect.position.clone();
			}
			if (!intersect.name.includes("Sprite")) {
				interactedObjects.push(intersect);
			}
			const previousIntersect =
				interactedObjects.length > 1 ?
					interactedObjects[interactedObjects.length - 2]
				:	null;
			if (previousIntersect !== null) {
				new LineElement(previousIntersect, intersect, primaryColor, true);
			}
			const currentInstance = this.getInstance(intersect);
			if (currentInstance) {
				if (intersect.type === "Sprite") {
					if (intersect.slug && intersect.userData?.clicked) {
						if (intersect.slug === "endWalkthrough") {
							const titleFit = await this.fitTitleSprite();
							if (!titleFit) {
								return;
							}
						}
					} else if (intersect.slug) {
						await this.fitSpriteTarget(intersect);
					}

					if (intersect.name.includes("Sprite")) {
						switch (intersect.name) {
							case "wanderSprite":
								if (!intersect.userData.clicked) {
									this.experience.sceneOperations.initWander(intersect);
								}
								break;
							case "PathwayItemSprite":
								await this.fitSpriteTarget(intersect);
								if (!intersect.userData.clicked) {
									intersect.userData.clicked = true;
									const sceneDocId = intersect.userData.data?.id;
									const gltfUrl = intersect.userData.data?.gltfURL;
									debugLog("[pathways] selected pathway sprite", {
										sceneDocId,
										hasGltfUrl: Boolean(gltfUrl),
									});
									if (!sceneDocId || !gltfUrl) {
										debugLog("[pathways] missing selection payload", intersect.userData.data);
										return;
									}
									const importedScene =
										await this.experience.importPath.loadGLTF(gltfUrl);
									debugLog("[pathways] gltf loaded for scene", sceneDocId);
									await this.experience.importPath.insertLoadedScene(
										importedScene,
										sceneDocId,
									);
									debugLog("[pathways] scene inserted", sceneDocId);
								}
								return;
							case " ? Sprite":
								this.experience.uiHandlers.toggleInfoPage();
								await this.fitSpriteTarget(intersect);
								break;
							case "anywhereSprite":
								currentInstance.changeSpriteColor(intersect, primaryColor, brown);
								const titleFit = await this.fitTitleSprite();
								if (!titleFit) {
									return;
								}
								this.experience.sceneOperations.emptyScene(this.scene);
								this.experience.uiHandlers.hideStartUI();
								this.experience.uiHandlers.setupPathUI();
								this.experience.sceneOperations.loadRandomBlock(this.scene);
								this.experience.cameraPathTracker = new CameraPathTracker(true);
								break;
							case "somewhereSprite":
								currentInstance.changeSpriteColor(intersect, primaryColor, brown);
								if (!intersect.userData.clicked) {
									debugLog("[pathways] entering somewhere flow");
									intersect.userData.direction = new THREE.Vector3();
									await this.fitSpriteTarget(intersect, 0);
									this.experience.uiHandlers.hideStartUI();
									await this.experience.sceneOperations.emptyScene(this.scene);
									debugLog("[pathways] scene emptied, opening pathways page");
									await this.experience.uiHandlers.togglePathwaysPage();
									debugLog("[pathways] pathways page populated");
								}
								break;
							default:
								break;
						}
						intersect.userData.clicked = true;
					} else {
						await this.fitSpriteTarget(intersect);
					}
				} else if (
					intersect.type === "Mesh" &&
					intersect.parent?.name !== "pathwayItem"
				) {
					this.camera.fitToRect(intersect, this.camera.paddingMesh);
				}
				await currentInstance.onClick();
				await this.experience.sceneOperations.fitCameraToUpcomingConnections();
			}
		}
	}
}
